<template>
    <div class="tab-pane active">
        <div v-if="locations.length > 0" class="table-wrapper no-print">
            <h5 v-if="searchKeyword" v-show="!searchKeyword || !nothingFound">
                {{ $t('roomConfiguration.search.wasFound') }} {{ countedItems[0]
                }}{{ $t('roomConfiguration.search.corridors') }}, {{ countedItems[1]
                }}{{ $t('roomConfiguration.search.rooms') }}, {{ countedItems[2]
                }}{{ $t('roomConfiguration.search.beds') }}, {{ countedItems[3]
                }}{{ $t('roomConfiguration.search.toilets') }}, {{ countedItems[4]
                }}{{ $t('roomConfiguration.search.modules') }}, {{ countedItems[5]
                }}{{ $t('roomConfiguration.search.others') }}
            </h5>
            <div v-show="!searchKeyword || !nothingFound" class="table-config">
                <div class="theader">
                    <div class="location">{{ $t('roomConfiguration.location') }}</div>
                    <div class="actions">{{ $t('roomConfiguration.actions') }}</div>
                    <div class="devices">{{ $t('roomConfiguration.devices') }}</div>
                </div>
                <div :class="['tbody', { 'hide-tree-lines': !!searchKeyword }]">
                    <Panel ref="panel" :location="locationsObj" />
                </div>
            </div>
            <h3 v-if="searchKeyword && nothingFound">
                {{ $t('common.searchNoResult', { val: searchKeyword }) }}
            </h3>
        </div>
        <div v-else class="data-empty">
            <h3 v-if="dataLoaded" v-text="$t('roomConfiguration.noRoomConfiguration')"></h3>
        </div>
        <div class="table-wrapper print-only">
            <h4 v-if="searchKeyword && !nothingFound">{{ $t('print.search', { val: searchKeyword }) }}</h4>
            <div v-if="locations.length > 0" class="table-config">
                <div class="theader">
                    <div class="location">{{ $t('roomConfiguration.location') }}</div>
                    <div class="actions">{{ $t('roomConfiguration.actions') }}</div>
                    <div class="devices">{{ $t('roomConfiguration.devices') }}</div>
                </div>
                <div class="tbody"><Panel :location="locationsObjCopy" :nothing-found="nothingFound" /></div>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import { mapActions, mapMutations, mapState } from 'vuex';
import Panel from './room-configuration/Panel.vue';
import Location from './room-configuration/Location';
import mappingState from './common/mappingState';
import { sortLocations, ifIsNotCanceled } from './common/helpers';
import { resourceModuleConfig, resourceRoomConfHistory } from '../api/resources';
import { getCancelToken } from '../api';
import { SUCCESS, MAX_MODULES } from '../constants';
import store from '../store';
import { transform, isEqual, cloneDeep } from 'lodash-es';

export default {
    components: {
        Panel,
    },

    mixins: [mappingState],

    data() {
        return {
            locations: [],
            locationsObj: new Location({}),
            locationsCopy: [],
            locationsObjCopy: new Location({}),
            rootLocationId: null,
            loc: [],
            dataLoaded: false,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState(['unsavedChanges']),

        searched() {
            return this.searchKeyword !== ''
                ? this.locations.filter(item => item.name.toLowerCase().includes(this.searchKeyword))
                : null;
        },

        countedItems() {
            let filteredItems = this.searched;
            let aaCounter = 0;
            let toiletCounter = 0;
            let bedCounter = 0;
            let roomCounter = 0;
            let modulesCounter = 0;
            let otherCounter = 0;
            if (filteredItems) {
                filteredItems.forEach(function(item) {
                    switch (item.type) {
                        case 1:
                            aaCounter++;
                            break;
                        case 8:
                            toiletCounter++;
                            break;
                        case 6:
                            roomCounter++;
                            break;
                        case 7:
                            bedCounter++;
                            break;
                        default:
                            otherCounter++;
                            break;
                    }
                });
            }
            if (this.searchKeyword !== '') {
                this.locations.forEach(item => {
                    if (Array.isArray(item.modules)) {
                        item.modules.forEach(module => {
                            if (module.name.toLowerCase().includes(this.searchKeyword)) {
                                modulesCounter++;
                            }
                        });
                    }
                });
            }

            return [aaCounter, roomCounter, bedCounter, toiletCounter, modulesCounter, otherCounter];
        },

        nothingFound() {
            return this.countedItems.reduce((sum, current) => sum + current, 0) === 0;
        },
    },

    watch: {
        locationsObj: {
            handler() {
                this.setUnsavedChanges(true);
            },
            deep: true,
        },
    },

    created() {
        this.updateData(() => {
            return this.loadLocations();
        });
        this.configButton({
            buttonName: 'saveButton',
            action: () => this.saveRoomConfiguration(),
            disabled: () => !this.unsavedChanges,
        });
        this.$nextTick(() => {
            this.loadLocations();
        });
    },

    beforeDestroy() {
        this.progressBarBeforeDestroy();
        this.cancelToken.cancel();
    },

    methods: {
        ...mapMutations({ updateData: 'SET_UPDATE_DATA' }),

        ...mapActions({
            progressBarStart: 'progressBar/start',
            progressBarUpdate: 'progressBar/update',
            progressBarHide: 'progressBar/hide',
            progressBarBeforeDestroy: 'progressBar/beforeDestroy',
        }),

        loadLocations() {
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
            this.showLoading(true);
            return resourceModuleConfig({
                id: this.userAreaId,
                cancelToken: this.cancelToken.token,
            })
                .then(response => {
                    this.locations = response.data ? sortLocations(response.data) : [];
                    this.locationsObj = this.generateLocationsObj(this.locations);
                    this.locationsCopy = cloneDeep(this.locations);
                    this.locationsObjCopy = this.generateLocationsObj(this.locationsCopy);
                    this.rootLocationId = this.locationsObj.location_id;
                    this.$nextTick(() => {
                        this.setUnsavedChanges(false);
                    });
                    this.showLoading(false);
                    this.setSearchEnabled(this.locations.length > 0);
                    this.dataLoaded = true;
                    return response;
                })
                .catch(
                    ifIsNotCanceled({
                        callback: () => {
                            this.locations = [];
                            this.dataLoaded = true;
                            this.setSearchEnabled(this.locations.length > 0);
                        },
                    })
                );
        },

        generateLocationsObj(locations) {
            let locationsObject = new Location(locations.find(item => Number(item.type) === 1));
            locationsObject.children = this.getNestedChildren(locations, locationsObject.location_id);

            return locationsObject;
        },

        getNestedChildren(locations, parent_id) {
            return locations
                .filter(l => l.parent_id === parent_id)
                .map(
                    l =>
                        new Location({
                            ...l,
                            children: this.getNestedChildren(locations, l.location_id),
                        })
                );
        },

        /**
         * Receives single-level list of Locations and splits into array of chunks
         * Chunk consists of locations array
         * Total quantity of modules in all locations inside the chunk is less than const MAX_MODULES
         * One location cannot be splitted if has quantity of modules more than const MAX_MODULES
         * @param list
         * @returns {Array}
         */
        getSplittedLocationsByMaximumModules(list) {
            let chunk = [];
            let countModulesInChunk = 0;
            let splittedSavingList = [];

            // This loop can make step back and re-iterate element
            for (let i = 0; i < list.length; i++) {
                // Current location
                let location = list[i];

                // Checks that the location has less modules than maximum
                if (location.getCountModules() <= MAX_MODULES) {
                    countModulesInChunk += location.getCountModules();

                    // If chunk is not too large, pushes this location in chunk
                    if (countModulesInChunk <= MAX_MODULES) {
                        chunk.push(location);

                        // if it is the last iterate, pushes chunk in common array of chunks.
                        if (i === list.length - 1) {
                            splittedSavingList.push(chunk);
                        }

                        // If chunk is too large, pushes chunk without last location in common array of chunks.
                        // Resets counter and chunk
                        // Makes step back
                    } else {
                        splittedSavingList.push(chunk);
                        countModulesInChunk = 0;
                        chunk = [];

                        // step back
                        i--;
                    }

                    // If this location has more modules than maximum, sets new chunk with only this location
                } else {
                    if (chunk.length > 0) {
                        splittedSavingList.push(chunk);
                        splittedSavingList.push([location]);
                    } else {
                        chunk.push(location);
                        splittedSavingList.push(chunk);
                    }
                    countModulesInChunk = 0;
                    chunk = [];
                }
            }

            return splittedSavingList;
        },

        /**
         * Executes ajax for saving locations chunk by chunk
         * @param chunks
         * @returns {Promise<*>}
         */
        async postChunks(chunks) {
            let processed = 0;
            let total = chunks.reduce((accumulator, chunk) => accumulator + chunk.length, 0);
            this.progressBarStart(this.$t('progressBar.saving'));
            for (let i = 0; i < chunks.length; i++) {
                if (store.state.progressBar.isCanceled) {
                    this.progressBarHide();
                    return Promise.reject('');
                }
                try {
                    // 1) Save Room Configuration first
                    let response = await resourceModuleConfig({
                        method: 'post',
                        id: this.userAreaId,
                        data: JSON.stringify(chunks[i].map(location => location.getSaveData())),
                        cancelToken: this.cancelToken.token,
                    });
                    // Here if Room Configuration chunk was saved successfully
                    processed += response.data.processed;
                    // 2) Now update what was saved
                    let history = [];
                    chunks[i].forEach(location => {
                        if (location.isChanged()) {
                            location.modules.forEach(module => {
                                let diff = transform(module.functions, (result, value, key) => {
                                    if (!isEqual(value, module.initFunctions[key])) {
                                        result[key] = value;
                                        module.initFunctions[key] = value;
                                    }
                                });
                                if (Object.keys(diff).length !== 0) {
                                    history.push({
                                        actions: diff,
                                        location_id: location.location_id,
                                        peripheral_id: module.peripheral_id,
                                    });
                                }
                            });
                            // if saving proccess is canceled, saved locations are shown in print version
                            this.locationsCopy.forEach((locationCopy, i) => {
                                if (location.location_id === locationCopy.location_id) {
                                    this.locationsCopy[i] = cloneDeep(location);
                                }
                            });
                        }
                    });
                    // 3) Now save history
                    await resourceRoomConfHistory({
                        method: 'post',
                        data: JSON.stringify(history),
                        cancelToken: this.cancelToken.token,
                    });
                } catch (error) {
                    this.progressBarHide();
                    return Promise.reject(error);
                }
                this.locationsObjCopy = this.generateLocationsObj(this.locationsCopy);
                this.progressBarUpdate((processed * 100.0) / total);
            }
            this.progressBarHide();
            // async function returns Promise.resolve(returned value) by default
            return { processed };
        },

        /**
         * Saves changed locations in room configuration
         */
        saveRoomConfiguration() {
            let savingList = this.locationsObj
                .flattenNestedChildren()
                .filter(location => location.isChanged());
            let chunkList = this.getSplittedLocationsByMaximumModules(savingList);
            this.$nextTick(() => {
                return this.postChunks(chunkList)
                    .then(() => {
                        this.setUnsavedChanges(false);
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('common.saved'),
                        });
                    })
                    .catch(ifIsNotCanceled());
            });
        },
    },
};
</script>
